import { MaybeRefOrGetter } from 'vue'

const searchEndpoint = 'https://api.metagrid.ch/search?group=1&skip=0&take=1000'
const providersEndpoint = 'https://api.metagrid.ch/providers'

export type MetagridResource = {
  provider: string
  title: string
  providerSlug: string
  url: string
  category: string | undefined
}

let metagridProviderCache: any = undefined
const metagridResourceCache = new Map<string, any>()

export function useMetagrid(gnd: MaybeRefOrGetter<string | undefined>) {
  const optionsStore = useOptionsStore()

  const hasGnd = computed(() => typeof toValue(gnd) === 'string')

  const searchUrl = computed(() => {
    if (!hasGnd.value) return ''

    const gndValue = toValue(gnd)!
    const url = new URL(searchEndpoint)
    url.searchParams.set('query', gndValue)
    return url.toString()
  })

  const {
    data: providers,
    isFetching: isFetchingProviders,
    error: providerError,
  } = useFetch(providersEndpoint, {
    beforeFetch({ cancel }) {
      if (metagridProviderCache != null) {
        providers.value = metagridProviderCache
        cancel()
      }
    },
    afterFetch(ctx) {
      metagridProviderCache = ctx.data
      return ctx
    },
  }).json()
  const providerNamesMap = computed(() => {
    const map = new Map<string, string>()
    if (!providers.value) return map

    for (const provider of providers.value.providers) {
      const germanDetails = () =>
        provider.provider_details.find((details: any) => details.lang === 'de')
      const englishDetails = () =>
        provider.provider_details.find((details: any) => details.lang === 'en')
      const anyDetails = () =>
        provider.provider_details.find((details: any) => details.fallback === true)
      const details = germanDetails() ?? englishDetails() ?? anyDetails()
      map.set(provider.slug, details.name)
    }

    return map
  })

  const {
    data,
    isFetching: isFetchingResources,
    error: resourceError,
  } = useFetch(searchUrl, {
    refetch: hasGnd,
    immediate: hasGnd.value,
    beforeFetch({ url, cancel }) {
      if (metagridResourceCache.has(url)) {
        data.value = metagridResourceCache.get(url)!
        cancel()
        return
      }
    },
    afterFetch(ctx) {
      metagridResourceCache.set(ctx.response.url, ctx.data)
      return ctx
    },
  }).json()

  const resources = computed<Array<{ provider: string; url: string }>>(() => {
    if (!data.value) return []

    return data.value.concordances
      .flatMap((concordance: any) => concordance.resources)
      .filter((resource: any) => resource.link.rel === 'resource')
      .map((resource: any) => ({ provider: resource.provider.slug, url: resource.link.uri }))
  })

  const enrichedResources = computed<MetagridResource[]>(() => {
    if (providerNamesMap.value.size === 0) return []

    return resources.value.map((resource: any) => {
      providerNamesMap
      const provider = providerNamesMap.value.get(resource.provider) || resource.provider
      return {
        ...resource,
        providerSlug: resource.provider,
        category: optionsStore.metagrid[resource.provider]?.category ?? undefined,
        provider,
        title: optionsStore.metagrid[resource.provider]?.title || provider,
      }
    })
  })

  return {
    resources: enrichedResources,
    error: computed(() => providerError.value || resourceError.value),
    isFetching: computed(() => isFetchingProviders.value || isFetchingResources.value),
  }
}
